"use client";

import React from "react";
import Image from "next/image";
import { ArrowLeft } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import { useAction } from "next-safe-action/hooks";

import Link from "@/components/link";
import { Skeleton } from "@/components/ui/skeleton";

import { fetchHomeBanners } from "@/fetchers/homepage";

import SwiperNav from "@/components/swiper-nav";
import useMoengageEventTracker from "@/hooks/use-moengage";

import EVENT_NAME from "@/hooks/use-moengage/event-name";
import EVENT_ATTRIBUTE from "@/hooks/use-moengage/event-attribute";

function HomeSlider() {
  const trackMoengageEvent = useMoengageEventTracker();

  const swiperPaginationRef = React.useRef();

  const swiperNextRef = React.useRef();
  const swiperPrevRef = React.useRef();

  // State for swiper instance
  const [swiper, setSwiper] = React.useState();

  // Action to fetch homepage banners
  const {
    hasSucceeded,
    result,
    execute: fetchBanners,
  } = useAction(fetchHomeBanners);

  // Fetch Homepage Banners
  React.useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  // Update navigation buttons when the swiper instance changes
  React.useEffect(() => {
    if (swiper?.params && result?.data?.length) {
      swiper.pagination.el = swiperPaginationRef.current;
      swiper.pagination.render();
    }
  }, [swiper, result]);

  // Render skeleton while loading
  if (!hasSucceeded) {
    return (
      <div className="flex gap-x-4.5 h-72 md:h-40 mt-4 md:mt-9 overflow-hidden">
        <Skeleton className="hidden md:block md:flex-1" />
        <Skeleton className="w-screen md:w-container-studyiq shrink-0" />
        <Skeleton className="hidden md:block md:flex-1" />
      </div>
    );
  }

  // Handle click event to track MoEngage Event
  const handleOnClick = (slidePosition) => () => {
    trackMoengageEvent(EVENT_NAME.DFP_CLICKED);
    trackMoengageEvent(EVENT_NAME.BANNER_CLICKED, {
      [EVENT_ATTRIBUTE.POSITION]: slidePosition,
      [EVENT_ATTRIBUTE.SOURCE_SCREEN]: "home",
    });
  };

  return (
    <section
      id="home-slider"
      data-testid="home-slider-wrapper"
      className="relative w-full flex flex-col-reverse gap-y-3"
    >
      <div className="container-studyiq mx-auto">
        <div
          ref={swiperPaginationRef}
          className="flex gap-x-1.5 justify-center"
        />
      </div>

      <Swiper
        modules={[Pagination, Navigation, Autoplay]}
        slidesPerView="auto"
        spaceBetween={18}
        autoplay={{
          delay: 3000,
          pauseOnMouseEnter: true,
        }}
        pagination={{
          el: swiperPaginationRef.current,
          clickable: true,
          bulletClass:
            "inline-block size-2 border border-grayscale-line rounded-full cursor-pointer",
          bulletActiveClass: "bg-secondary border-secondary",
        }}
        centeredSlides
        loop
        onSwiper={setSwiper}
        className="max-w-screen mt-4 md:mt-9"
        data-testid="home-slider-swiper"
      >
        {result?.data?.map((banner, index) => (
          <SwiperSlide
            key={banner.id}
            data-testid="home-slider-slide"
            className="max-w-screen md:max-w-container-studyiq"
          >
            <Link
              href={banner.bannerLink || `/course-detail/${banner.courseSlug}`}
              className="block overflow-hidden md:rounded-xl"
              onClick={handleOnClick(index)}
            >
              {banner.mwebImg && (
                <Image
                  src={banner.mwebImg}
                  alt={banner.name}
                  width={480}
                  height={398}
                  className="block md:hidden aspect-480/398"
                  priority
                />
              )}

              <Image
                src={banner.imgPath}
                alt={banner.name}
                width={966}
                height={160}
                className="hidden md:block aspect-966/160"
                priority
              />
            </Link>
          </SwiperSlide>
        ))}
      </Swiper>

      <SwiperNav
        swiper={swiper}
        nextNavRef={swiperNextRef}
        prevNavRef={swiperPrevRef}
        navIcon={ArrowLeft}
        iconClassName="bg-black/90 text-primary-foreground!"
        className="lg:hidden absolute top-1/2 z-20 w-full justify-between px-2 -translate-y-1/2"
      />
    </section>
  );
}

export default HomeSlider;
